import React, { useState } from 'react';
import { X, Clock, ChevronDown, ChevronUp, ArrowRight, User, Loader2 } from 'lucide-react';
import { HistoryEntry } from './types';


type HistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  history: HistoryEntry[];
  loading?: boolean;
};

const HistoryEntryCard: React.FC<{ entry: HistoryEntry }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(entry.date);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden transition-all hover:shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-4">
          <div className="bg-blue-50 p-2 rounded-lg">
            <Clock className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}{' '}
              <span className="text-gray-400 font-normal">
                {date.toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <User className="w-3 h-3 text-gray-400" />
              <p className="text-xs text-gray-500">{entry.user}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            {entry.changes.length} alteraç{entry.changes.length !== 1 ? 'ões' : 'ão'}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          <div className="divide-y divide-gray-100">
            {entry.changes.map((change, idx) => (
              <div key={idx} className="px-5 py-3.5 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {change.nomeFantasia || change.razaoSocial || change.cnpjLojista}
                  </p>
                  <p className="text-xs text-gray-400 font-mono">CNPJ: {change.cnpjLojista}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                    change.valorAnterior
                      ? 'bg-red-50 text-red-700 border border-red-100'
                      : 'bg-gray-50 text-gray-500 border border-gray-200'
                  }`}>
                    {change.valorAnterior || '(vazio)'}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {change.valorNovo || '(vazio)'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  history,
  loading = false,
}) => {
  const [visible, setVisible] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const totalChanges = history.reduce((acc, e) => acc + e.changes.length, 0);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Histórico de Alterações"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-[95vw] max-w-2xl max-h-[85vh] flex flex-col transition-all duration-300 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Histórico de Alterações
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {history.length} registro{history.length !== 1 ? 's' : ''} | {totalChanges} {totalChanges !== 1 ? 'alterações' : 'alteração'} no total
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
              <p className="text-sm text-gray-500">Carregando histórico...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="bg-gray-100 rounded-full p-4 mb-3">
                <Clock className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">Nenhum histórico disponível</p>
            </div>
          ) : (
            history.map((entry) => (
              <HistoryEntryCard key={entry.id} entry={entry} />
            ))
          )}
        </div>

        <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <span className="text-xs text-gray-400">Pressione ESC para fechar</span>
        </div>
      </div>
    </div>
  );
};
